'use client';

import React from 'react';
import { cx, sortCx } from '../../../utils/cx';

export type LoadingSpinnerProps = {
    /** The square outer size of the spinner. */
    size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
    /** Additional classes for positioning or colour inheritance. */
    className?: string;
};

const defaultStyle = {
    root: 'size-5',
    line: '',
    dotRadius: '1',
    viewBox: '11.95 11.95 17.1 17.1',
};

const styles = sortCx({
    size: {
        xxs: {
            ...defaultStyle,
            root: 'size-3',
            line: 'stroke-[2.92]',
            dotRadius: '1.46',
            viewBox: '11.49 11.49 18.02 18.02',
        },
        xs: {
            ...defaultStyle,
            root: 'size-4',
        },
        sm: {
            ...defaultStyle,
            root: 'size-5',
        },
        md: {
            ...defaultStyle,
            root: 'size-6',
        },
        lg: {
            ...defaultStyle,
            root: 'size-8',
        },
        xl: {
            ...defaultStyle,
            root: 'size-12',
        },
        '2xl': {
            ...defaultStyle,
            root: 'size-14',
        },
        '3xl': {
            ...defaultStyle,
            root: 'size-16',
        },
    },
    line: 'fill-none stroke-current stroke-[2] [stroke-dasharray:7_22] [stroke-dashoffset:7]',
    faded: 'fill-none stroke-current stroke-[2] opacity-30',
    circle: 'origin-center animate-loading-spinner-dot fill-current stroke-none',
});

const ANIMATED_LINES = [
    {
        coordinates: { x1: '27.8', y1: '13.2', x2: '27.8', y2: '27.8' },
        animationClass: 'animate-loading-spinner-line-1',
    },
    {
        coordinates: { x1: '27.8', y1: '27.8', x2: '13.2', y2: '27.8' },
        animationClass: 'animate-loading-spinner-line-2',
    },
    {
        coordinates: { x1: '13.2', y1: '27.8', x2: '13.2', y2: '13.2' },
        animationClass: 'animate-loading-spinner-line-3',
    },
    {
        coordinates: { x1: '13.2', y1: '13.2', x2: '27.8', y2: '13.2' },
        animationClass: 'animate-loading-spinner-line-4',
    },
] as const;

/** Renders the Milanote square loading spinner. */
export const LoadingSpinner = ({ size = 'sm', className }: LoadingSpinnerProps) => {
    const sizeStyles = styles.size[size];
    const lineClassName = cx(styles.line, sizeStyles.line);

    return (
        <svg
            aria-hidden="true"
            data-icon="loading"
            fill="none"
            viewBox={sizeStyles.viewBox}
            shapeRendering="geometricPrecision"
            className={cx('block shrink-0 overflow-visible text-fg-tertiary', sizeStyles.root, className)}
        >
            <rect
                className={cx(styles.faded, sizeStyles.line)}
                x="13.2"
                y="13.2"
                width="14.6"
                height="14.6"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            {ANIMATED_LINES.map((line, index) => {
                const { coordinates, animationClass } = line;
                return (
                    <line
                        key={`line-${index}`}
                        className={cx(lineClassName, animationClass)}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        {...coordinates}
                    />
                );
            })}

            {ANIMATED_LINES.map((line, index) => {
                const { coordinates, animationClass } = line;
                return (
                    <line
                        key={`line-delayed-${index}`}
                        className={cx(lineClassName, animationClass, '[animation-delay:-1.2s]')}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        {...coordinates}
                    />
                );
            })}

            <circle className={cx(styles.circle)} cx="27.8" cy="13.2" r={sizeStyles.dotRadius} />
            <circle
                className={cx(styles.circle, '[animation-delay:-1.2s]')}
                cx="27.8"
                cy="13.2"
                r={sizeStyles.dotRadius}
            />
        </svg>
    );
};

LoadingSpinner.displayName = 'LoadingSpinner';
